/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import {
  getFinanceEntries,
  createFinanceEntry,
  updateFinanceEntry,
  deleteFinanceEntry,
  getFinanceCategories,
  createFinanceCategory,
  archiveFinanceCategory,
  getFinanceStats,
  previewFinanceStatement,
  confirmFinanceStatement,
  getFinanceChatHistory,
  financeChatStream,
  getProjects,
  getUsers
} from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Skaidra nauda' },
  { value: 'card', label: 'Karte' }
];

const ENTRY_TYPES = [
  { value: 'income', label: 'Ieņēmums' },
  { value: 'expense', label: 'Izdevums' }
];

const FinanceManagement = () => {
  const { t } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({ monthly: [], daily: [], top_categories: [] });
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    date: '',
    entry_type: 'expense',
    amount: '',
    category_id: '',
    description: '',
    project_id: '',
    client_id: '',
    payment_method: 'card',
    target: ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryPurpose, setCategoryPurpose] = useState('');
  const [statementPreview, setStatementPreview] = useState(null);
  const [statementEntries, setStatementEntries] = useState([]);
  const [statementFile, setStatementFile] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const sessionId = useMemo(() => {
    const saved = localStorage.getItem('financeChatSession');
    if (saved) return saved;
    const next = `finance-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('financeChatSession', next);
    return next;
  }, []);

  const clientMap = useMemo(() => {
    const map = {};
    clients.forEach((client) => {
      map[client.id] = client.client_name;
    });
    return map;
  }, [clients]);

  const projectMap = useMemo(() => {
    const map = {};
    projects.forEach((project) => {
      map[project.id] = project.name;
    });
    return map;
  }, [projects]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [entryData, categoryData, statsData, projectData, userData] = await Promise.all([
        getFinanceEntries(),
        getFinanceCategories(),
        getFinanceStats(),
        getProjects(),
        getUsers()
      ]);
      setEntries(entryData || []);
      setCategories(categoryData || []);
      setStats(statsData || { monthly: [], daily: [], top_categories: [] });
      setProjects(projectData || []);
      setClients((userData || []).filter((user) => user.role === "client"));
    } catch (error) {
      toast.error(t('portalUi.admin.finance.toastLoadError', 'Neizdevās ielādēt finanšu datus'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    const loadChat = async () => {
      try {
        const history = await getFinanceChatHistory(sessionId);
        if (history && history.length) {
          setChatMessages(history.map((msg) => ({ role: msg.role, text: msg.content })));
        }
      } catch (error) {
        // silent fail
      }
    };
    loadChat();
  }, [sessionId]);

  const openModal = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        date: entry.date?.split('T')[0] || '',
        entry_type: entry.entry_type,
        amount: entry.amount,
        category_id: entry.category_id || '',
        description: entry.description || '',
        project_id: entry.project_id || '',
        client_id: entry.client_id || '',
        payment_method: entry.payment_method || 'card',
        target: entry.target || ''
      });
    } else {
      setEditingEntry(null);
      setFormData({
        date: '',
        entry_type: 'expense',
        amount: '',
        category_id: '',
        description: '',
        project_id: '',
        client_id: '',
        payment_method: 'card',
        target: ''
      });
    }
    setIsModalOpen(true);
  };

  const saveEntry = async (e) => {
    e.preventDefault();
    if (!formData.date || !formData.amount || !formData.category_id) {
      toast.error(t('portalUi.admin.finance.toastValidation', 'Aizpildiet obligātos laukus'));
      return;
    }
    try {
      const selectedCategory = categories.find((cat) => cat.id === formData.category_id);
      const selectedClient = clients.find((client) => client.id === formData.client_id);
      const selectedProject = projects.find((project) => project.id === formData.project_id);
      const payload = {
        ...formData,
        amount: Number(formData.amount),
        category_name: selectedCategory?.name || '',
        client_name: selectedClient?.client_name || '',
        project_name: selectedProject?.name || ''
      };
      if (editingEntry) {
        await updateFinanceEntry(editingEntry.id, payload);
        toast.success(t('portalUi.admin.finance.toastUpdated', 'Ieraksts atjaunināts'));
      } else {
        await createFinanceEntry(payload);
        toast.success(t('portalUi.admin.finance.toastCreated', 'Ieraksts pievienots'));
      }
      setIsModalOpen(false);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portalUi.admin.finance.toastSaveError', 'Kļūda saglabājot ierakstu'));
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm(t('portalUi.admin.finance.confirmDelete', 'Dzēst ierakstu?'))) return;
    try {
      await deleteFinanceEntry(entryId);
      toast.success(t('portalUi.admin.finance.toastDeleted', 'Ieraksts dzēsts'));
      loadAll();
    } catch (error) {
      toast.error(t('portalUi.admin.finance.toastDeleteError', 'Neizdevās dzēst ierakstu'));
    }
  };

  const addCategory = async () => {
    if (!categoryName.trim()) {
      toast.error(t('portalUi.admin.finance.categoryValidation', 'Ievadiet kategorijas nosaukumu'));
      return;
    }
    try {
      await createFinanceCategory({ name: categoryName, description: categoryPurpose });
      toast.success(t('portalUi.admin.finance.categoryCreated', 'Kategorija pievienota'));
      setCategoryName('');
      setCategoryPurpose('');
      loadAll();
    } catch (error) {
      toast.error(t('portalUi.admin.finance.categoryError', 'Neizdevās pievienot kategoriju'));
    }
  };

  const removeCategory = async (categoryId) => {
    if (!window.confirm(t('portalUi.admin.finance.categoryConfirmDelete', 'Arhivēt kategoriju?'))) return;
    await archiveFinanceCategory(categoryId);
    loadAll();
  };

  const updateStatementEntry = (index, field, value) => {
    setStatementEntries((prev) => prev.map((entry, idx) => {
      if (idx !== index) return entry;
      if (field === 'category_id') {
        const categoryName = categories.find((cat) => cat.id === value)?.name || '';
        return { ...entry, category_id: value, category_name: categoryName };
      }
      if (field === 'client_id') {
        const clientName = clientMap[value] || '';
        return { ...entry, client_id: value, client_name: clientName };
      }
      if (field === 'project_id') {
        const projectName = projectMap[value] || '';
        return { ...entry, project_id: value, project_name: projectName };
      }
      return { ...entry, [field]: value };
    }));
  };

  const toggleStatementEntry = (index) => {
    setStatementEntries((prev) => prev.map((entry, idx) => (
      idx === index ? { ...entry, selected: !entry.selected } : entry
    )));
  };

  const handleStatementPreview = async () => {
    if (!statementFile) {
      toast.error(t('portalUi.admin.finance.statementValidation', 'Izvēlieties PDF failu'));
      return;
    }
    try {
      const preview = await previewFinanceStatement(statementFile);
      setStatementPreview(preview);
      setStatementEntries((preview.extracted_entries || []).map((entry, index) => ({
        ...entry,
        local_id: `preview-${index}`,
        selected: true,
        payment_method: entry.payment_method || 'card',
        client_id: entry.client_id || '',
        project_id: entry.project_id || '',
        target: entry.target || ''
      })));
      toast.success(t('portalUi.admin.finance.statementPreview', 'Priekšskatījums gatavs'));
    } catch (error) {
      toast.error(t('portalUi.admin.finance.statementError', 'Neizdevās apstrādāt PDF'));
    }
  };

  const confirmImport = async () => {
    if (!statementPreview) return;
    try {
      const selectedEntries = statementEntries
        .filter((entry) => entry.selected)
        .map(({ local_id, selected, ...rest }) => rest);
      await confirmFinanceStatement(statementPreview.id, { entries: selectedEntries });
      toast.success(t('portalUi.admin.finance.statementImported', 'Ieraksti importēti'));
      setStatementPreview(null);
      setStatementFile(null);
      loadAll();
    } catch (error) {
      toast.error(t('portalUi.admin.finance.statementImportError', 'Neizdevās importēt'));
    }
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim()) return;
    const userMessage = chatInput;
    setChatMessages((prev) => [...prev, { role: 'user', text: userMessage }]);
    setChatInput('');
    setChatLoading(true);

    let assistantText = '';
    setChatMessages((prev) => [...prev, { role: 'assistant', text: '' }]);

    try {
      await financeChatStream(sessionId, userMessage, (chunk) => {
        assistantText += chunk;
        setChatMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', text: assistantText };
          return updated;
        });
      });
    } catch (error) {
      toast.error(t('portalUi.admin.finance.chatError', 'Neizdevās saņemt AI atbildi'));
    } finally {
      setChatLoading(false);
    }
  };

  const monthlyChartData = useMemo(() => {
    const map = {};
    stats.monthly.forEach((item) => {
      const key = `${item.year}-${String(item.month).padStart(2, '0')}`;
      if (!map[key]) {
        map[key] = { month: key, income: 0, expense: 0 };
      }
      map[key][item.type] = item.total;
    });
    return Object.values(map).map((item) => ({ ...item, profit: (item.income || 0) - (item.expense || 0) }));
  }, [stats]);

  const dailyChartData = useMemo(() => {
    const map = {};
    stats.daily.forEach((item) => {
      const key = `${item.year}-${String(item.month).padStart(2, '0')}-${String(item.day).padStart(2, '0')}`;
      if (!map[key]) {
        map[key] = { day: key, income: 0, expense: 0 };
      }
      map[key][item.type] = item.total;
    });
    return Object.values(map).map((item) => ({ ...item, profit: (item.income || 0) - (item.expense || 0) }));
  }, [stats]);

  return (
    <div className="space-y-6" data-testid="admin-finance-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1F3D2B]" data-testid="admin-finance-title">
            {t('portalUi.admin.finance.title', 'Ieņēmumi un izdevumi')}
          </h1>
          <p className="text-[#2E2E2E]/70" data-testid="admin-finance-subtitle">
            {t('portalUi.admin.finance.subtitle', 'Pievienojiet ierakstus, analizējiet statistiku un ieteikumus')}
          </p>
        </div>
        <Button onClick={() => openModal()} data-testid="admin-finance-add">
          {t('portalUi.admin.finance.addEntry', 'Pievienot ierakstu')}
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>{t('portalUi.admin.finance.statsTitle', 'Statistika pa mēnešiem')}</CardTitle>
          </CardHeader>
          <CardContent className="h-72" data-testid="finance-monthly-chart">
            {monthlyChartData.length === 0 ? (
              <p className="text-sm text-[#2E2E2E]/60" data-testid="finance-monthly-empty">
                {t('portalUi.admin.finance.noStats', 'Nav datu')}
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="income" name="Ieņēmumi" stroke="#16a34a" strokeWidth={2} />
                  <Line type="monotone" dataKey="expense" name="Izdevumi" stroke="#dc2626" strokeWidth={2} />
                  <Line type="monotone" dataKey="profit" name="Peļņa" stroke="#2563eb" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t('portalUi.admin.finance.topCategories', 'Top kategorijas')}</CardTitle>
          </CardHeader>
          <CardContent data-testid="finance-top-categories">
            <div className="space-y-3">
              {stats.top_categories.length === 0 ? (
                <p className="text-sm text-[#2E2E2E]/60" data-testid="finance-top-categories-empty">{t('portalUi.admin.finance.noCategories', 'Nav datu')}</p>
              ) : (
                stats.top_categories.map((item) => (
                  <div key={item.category} className="flex items-center justify-between text-sm" data-testid={`finance-top-${item.category}`}>
                    <span>{item.category}</span>
                    <span className="font-semibold">€{item.total.toFixed(2)}</span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('portalUi.admin.finance.dailyStats', 'Statistika pa dienām')}</CardTitle>
          </CardHeader>
          <CardContent className="h-72" data-testid="finance-daily-chart">
            {dailyChartData.length === 0 ? (
              <p className="text-sm text-[#2E2E2E]/60" data-testid="finance-daily-empty">
                {t('portalUi.admin.finance.noStats', 'Nav datu')}
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="income" name="Ieņēmumi" stroke="#16a34a" strokeWidth={2} />
                  <Line type="monotone" dataKey="expense" name="Izdevumi" stroke="#dc2626" strokeWidth={2} />
                  <Line type="monotone" dataKey="profit" name="Peļņa" stroke="#2563eb" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('portalUi.admin.finance.entriesTitle', 'Ieraksti')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-[#2E2E2E]/60" data-testid="finance-entries-loading">{t('portalUi.common.loading', 'Ielādē...')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="finance-table">
                <thead className="bg-[#f8faf8]">
                  <tr>
                    <th className="text-left px-4 py-3">{t('portalUi.admin.finance.table.date', 'Datums')}</th>
                    <th className="text-left px-4 py-3">{t('portalUi.admin.finance.table.type', 'Tips')}</th>
                    <th className="text-left px-4 py-3">{t('portalUi.admin.finance.table.category', 'Kategorija')}</th>
                    <th className="text-left px-4 py-3">{t('portalUi.admin.finance.table.description', 'Apraksts')}</th>
                    <th className="text-right px-4 py-3">{t('portalUi.admin.finance.table.amount', 'Summa')}</th>
                    <th className="text-left px-4 py-3">{t('portalUi.admin.finance.table.payment', 'Maksājums')}</th>
                    <th className="text-left px-4 py-3">{t('portalUi.admin.finance.table.client', 'Klients')}</th>
                    <th className="text-left px-4 py-3">{t('portalUi.admin.finance.table.project', 'Projekts')}</th>
                    <th className="text-left px-4 py-3">{t('portalUi.admin.finance.table.target', 'Kam paredzēts')}</th>
                    <th className="text-right px-4 py-3">{t('portalUi.admin.finance.table.actions', 'Darbības')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((entry) => (
                    <tr key={entry.id} data-testid={`finance-row-${entry.id}`}>
                      <td className="px-4 py-3" data-testid={`finance-row-${entry.id}-date`}>{entry.date ? new Date(entry.date).toLocaleDateString('lv-LV') : '-'}</td>
                      <td className="px-4 py-3" data-testid={`finance-row-${entry.id}-type`}>{ENTRY_TYPES.find((t) => t.value === entry.entry_type)?.label}</td>
                      <td className="px-4 py-3" data-testid={`finance-row-${entry.id}-category`}>{entry.category_name || '-'}</td>
                      <td className="px-4 py-3" data-testid={`finance-row-${entry.id}-description`}>{entry.description || '-'}</td>
                      <td className="px-4 py-3 text-right" data-testid={`finance-row-${entry.id}-amount`}>€{Number(entry.amount).toFixed(2)}</td>
                      <td className="px-4 py-3" data-testid={`finance-row-${entry.id}-payment`}>{PAYMENT_METHODS.find((m) => m.value === entry.payment_method)?.label}</td>
                      <td className="px-4 py-3" data-testid={`finance-row-${entry.id}-client`}>{entry.client_name || clientMap[entry.client_id] || '-'}</td>
                      <td className="px-4 py-3" data-testid={`finance-row-${entry.id}-project`}>{entry.project_name || projectMap[entry.project_id] || '-'}</td>
                      <td className="px-4 py-3" data-testid={`finance-row-${entry.id}-target`}>{entry.target || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => openModal(entry)} data-testid={`finance-edit-${entry.id}`}>
                            {t('portalUi.admin.finance.edit', 'Rediģēt')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(entry.id)} data-testid={`finance-delete-${entry.id}`}>
                            {t('portalUi.admin.finance.delete', 'Dzēst')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('portalUi.admin.finance.categoriesTitle', 'Kategorijas')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label data-testid="finance-category-name-label">{t('portalUi.admin.finance.categoryName', 'Nosaukums')}</Label>
                <Input value={categoryName} onChange={(e) => setCategoryName(e.target.value)} data-testid="finance-category-name" />
              </div>
              <div>
                <Label data-testid="finance-category-purpose-label">{t('portalUi.admin.finance.categoryPurpose', 'Kam paredzēts')}</Label>
                <Input value={categoryPurpose} onChange={(e) => setCategoryPurpose(e.target.value)} data-testid="finance-category-description" />
              </div>
            </div>
            <Button onClick={addCategory} data-testid="finance-category-add">
              {t('portalUi.admin.finance.addCategory', 'Pievienot kategoriju')}
            </Button>
            <div className="space-y-2">
              {categories.map((cat) => (
                <div key={cat.id} className="flex items-center justify-between border rounded-lg px-3 py-2" data-testid={`finance-category-${cat.id}`}>
                  <div>
                    <div className="text-sm font-semibold">{cat.name}</div>
                    <div className="text-xs text-[#2E2E2E]/60">{cat.description || '-'}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => removeCategory(cat.id)} data-testid={`finance-category-archive-${cat.id}`}>
                    {t('portalUi.admin.finance.archiveCategory', 'Arhivēt')}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('portalUi.admin.finance.statementTitle', 'Bankas izdruku imports')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input type="file" accept="application/pdf" onChange={(e) => setStatementFile(e.target.files?.[0])} data-testid="finance-statement-file" />
            <Button onClick={handleStatementPreview} data-testid="finance-statement-preview">
              {t('portalUi.admin.finance.previewStatement', 'Veidot priekšskatījumu')}
            </Button>
            {statementPreview && (
              <div className="space-y-3" data-testid="finance-statement-preview">
                <p className="text-sm text-[#2E2E2E]/60" data-testid="finance-preview-count">
                  {t('portalUi.admin.finance.previewCount', 'Atrasti')} {statementEntries.length} {t('portalUi.admin.finance.previewEntries', 'ieraksti')}
                </p>
                <div className="max-h-60 overflow-auto border rounded-lg">
                  <table className="w-full text-xs" data-testid="finance-preview-table">
                    <thead className="bg-[#f8faf8]">
                      <tr>
                        <th className="px-3 py-2 text-left">{t('portalUi.admin.finance.table.actions', 'Darbības')}</th>
                        <th className="px-3 py-2 text-left">{t('portalUi.admin.finance.table.date', 'Datums')}</th>
                        <th className="px-3 py-2 text-left">{t('portalUi.admin.finance.table.type', 'Tips')}</th>
                        <th className="px-3 py-2 text-left">{t('portalUi.admin.finance.table.category', 'Kategorija')}</th>
                        <th className="px-3 py-2 text-left">{t('portalUi.admin.finance.table.description', 'Apraksts')}</th>
                        <th className="px-3 py-2 text-left">{t('portalUi.admin.finance.table.payment', 'Maksājums')}</th>
                        <th className="px-3 py-2 text-left">{t('portalUi.admin.finance.table.client', 'Klients')}</th>
                        <th className="px-3 py-2 text-left">{t('portalUi.admin.finance.table.project', 'Projekts')}</th>
                        <th className="px-3 py-2 text-right">{t('portalUi.admin.finance.table.amount', 'Summa')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statementEntries.map((entry, index) => (
                        <tr key={entry.local_id} data-testid={`finance-preview-${entry.local_id}`}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={!!entry.selected}
                              onChange={() => toggleStatementEntry(index)}
                              data-testid={`finance-preview-toggle-${entry.local_id}`}
                            />
                          </td>
                          <td className="px-3 py-2" data-testid={`finance-preview-${entry.local_id}-date`}>{entry.date ? new Date(entry.date).toLocaleDateString('lv-LV') : '-'}</td>
                          <td className="px-3 py-2">
                            <Select value={entry.entry_type} onValueChange={(value) => updateStatementEntry(index, 'entry_type', value)}>
                              <SelectTrigger data-testid={`finance-preview-type-${entry.local_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ENTRY_TYPES.map((item) => (
                                  <SelectItem key={item.value} value={item.value} data-testid={`finance-preview-type-${entry.local_id}-${item.value}`}>
                                    {item.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2">
                            <Select value={entry.category_id || 'none'} onValueChange={(value) => updateStatementEntry(index, 'category_id', value === 'none' ? '' : value)}>
                              <SelectTrigger data-testid={`finance-preview-category-${entry.local_id}`}>
                                <SelectValue placeholder={t('portalUi.admin.finance.form.categoryPlaceholder', 'Izvēlieties kategoriju')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none" data-testid={`finance-preview-category-none-${entry.local_id}`}>-</SelectItem>
                                {categories.map((cat) => (
                                  <SelectItem key={cat.id} value={cat.id} data-testid={`finance-preview-category-${entry.local_id}-${cat.id}`}>
                                    {cat.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2" data-testid={`finance-preview-${entry.local_id}-description`}>{entry.description || '-'}</td>
                          <td className="px-3 py-2">
                            <Select value={entry.payment_method || 'card'} onValueChange={(value) => updateStatementEntry(index, 'payment_method', value)}>
                              <SelectTrigger data-testid={`finance-preview-payment-${entry.local_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PAYMENT_METHODS.map((item) => (
                                  <SelectItem key={item.value} value={item.value} data-testid={`finance-preview-payment-${entry.local_id}-${item.value}`}>
                                    {item.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2">
                            <Select value={entry.client_id || 'none'} onValueChange={(value) => updateStatementEntry(index, 'client_id', value === 'none' ? '' : value)}>
                              <SelectTrigger data-testid={`finance-preview-client-${entry.local_id}`}>
                                <SelectValue placeholder={t('portalUi.admin.finance.form.clientPlaceholder', 'Izvēlieties klientu')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none" data-testid={`finance-preview-client-none-${entry.local_id}`}>-</SelectItem>
                                {clients.map((client) => (
                                  <SelectItem key={client.id} value={client.id} data-testid={`finance-preview-client-${entry.local_id}-${client.id}`}>
                                    {client.client_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2">
                            <Select value={entry.project_id || 'none'} onValueChange={(value) => updateStatementEntry(index, 'project_id', value === 'none' ? '' : value)}>
                              <SelectTrigger data-testid={`finance-preview-project-${entry.local_id}`}>
                                <SelectValue placeholder={t('portalUi.admin.finance.form.projectPlaceholder', 'Izvēlieties projektu')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none" data-testid={`finance-preview-project-none-${entry.local_id}`}>-</SelectItem>
                                {projects.map((project) => (
                                  <SelectItem key={project.id} value={project.id} data-testid={`finance-preview-project-${entry.local_id}-${project.id}`}>
                                    {project.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2 text-right" data-testid={`finance-preview-${entry.local_id}-amount`}>€{Number(entry.amount).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button onClick={confirmImport} data-testid="finance-statement-confirm">
                  {t('portalUi.admin.finance.confirmImport', 'Apstiprināt importu')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('portalUi.admin.finance.chatTitle', 'AI finanšu čats')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="h-64 overflow-y-auto border rounded-lg p-3 bg-white" data-testid="finance-chat-messages">
            {chatMessages.length === 0 ? (
              <p className="text-sm text-[#2E2E2E]/60" data-testid="finance-chat-empty">{t('portalUi.admin.finance.chatEmpty', 'Uzdod jautājumu par izdevumiem/ieņēmumiem')}</p>
            ) : (
              chatMessages.map((msg, index) => (
                <div key={index} className={`mb-3 ${msg.role === 'user' ? 'text-right' : 'text-left'}`} data-testid={`finance-chat-${msg.role}-${index}`}>
                  <div className={`inline-block px-3 py-2 rounded-lg text-sm ${msg.role === 'user' ? 'bg-[#1F3D2B] text-white' : 'bg-[#f4f6f4] text-[#2E2E2E]'}`}>
                    {msg.text}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <Textarea value={chatInput} onChange={(e) => setChatInput(e.target.value)} rows={2} data-testid="finance-chat-input" placeholder={t('portalUi.admin.finance.chatPlaceholder', 'Kur varam ietaupīt šomēnes?')} />
            <Button onClick={handleChatSubmit} disabled={chatLoading} data-testid="finance-chat-send">
              {chatLoading ? t('portalUi.admin.finance.chatSending', 'Domā...') : t('portalUi.admin.finance.chatSend', 'Sūtīt')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="finance-entry-dialog-description">
          <DialogHeader>
            <DialogTitle>{editingEntry ? t('portalUi.admin.finance.editEntry', 'Rediģēt ierakstu') : t('portalUi.admin.finance.addEntry', 'Pievienot ierakstu')}</DialogTitle>
            <DialogDescription id="finance-entry-dialog-description" className="sr-only">{t('portalUi.admin.finance.modalDescription', 'Ievadiet finanšu ierakstu')}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={saveEntry}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label data-testid="finance-form-date-label">{t('portalUi.admin.finance.form.date', 'Datums')}</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))} data-testid="finance-form-date" />
              </div>
              <div>
                <Label data-testid="finance-form-type-label">{t('portalUi.admin.finance.form.type', 'Tips')}</Label>
                <Select value={formData.entry_type} onValueChange={(value) => setFormData((prev) => ({ ...prev, entry_type: value }))}>
                  <SelectTrigger data-testid="finance-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTRY_TYPES.map((item) => (
                      <SelectItem key={item.value} value={item.value} data-testid={`finance-form-type-${item.value}`}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label data-testid="finance-form-amount-label">{t('portalUi.admin.finance.form.amount', 'Summa')}</Label>
                <Input type="number" value={formData.amount} onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))} data-testid="finance-form-amount" />
              </div>
              <div>
                <Label data-testid="finance-form-category-label">{t('portalUi.admin.finance.form.category', 'Kategorija')}</Label>
                <Select value={formData.category_id} onValueChange={(value) => setFormData((prev) => ({ ...prev, category_id: value }))}>
                  <SelectTrigger data-testid="finance-form-category">
                    <SelectValue placeholder={t('portalUi.admin.finance.form.categoryPlaceholder', 'Izvēlieties kategoriju')} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id} data-testid={`finance-form-category-${cat.id}`}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label data-testid="finance-form-target-label">{t('portalUi.admin.finance.form.target', 'Kam paredzēts')}</Label>
                <Input value={formData.target} onChange={(e) => setFormData((prev) => ({ ...prev, target: e.target.value }))} data-testid="finance-form-target" />
              </div>
              <div>
                <Label data-testid="finance-form-payment-label">{t('portalUi.admin.finance.form.payment', 'Maksājuma veids')}</Label>
                <Select value={formData.payment_method} onValueChange={(value) => setFormData((prev) => ({ ...prev, payment_method: value }))}>
                  <SelectTrigger data-testid="finance-form-payment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((item) => (
                      <SelectItem key={item.value} value={item.value} data-testid={`finance-form-payment-${item.value}`}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label data-testid="finance-form-client-label">{t('portalUi.admin.finance.form.client', 'Klients')}</Label>
                <Select value={formData.client_id || 'none'} onValueChange={(value) => setFormData((prev) => ({ ...prev, client_id: value === 'none' ? '' : value }))}>
                  <SelectTrigger data-testid="finance-form-client">
                    <SelectValue placeholder={t('portalUi.admin.finance.form.clientPlaceholder', 'Izvēlieties klientu')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="finance-form-client-none">-</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id} data-testid={`finance-form-client-${client.id}`}>
                        {client.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label data-testid="finance-form-project-label">{t('portalUi.admin.finance.form.project', 'Projekts')}</Label>
                <Select value={formData.project_id || 'none'} onValueChange={(value) => setFormData((prev) => ({ ...prev, project_id: value === 'none' ? '' : value }))}>
                  <SelectTrigger data-testid="finance-form-project">
                    <SelectValue placeholder={t('portalUi.admin.finance.form.projectPlaceholder', 'Izvēlieties projektu')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="finance-form-project-none">-</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id} data-testid={`finance-form-project-${project.id}`}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label data-testid="finance-form-description-label">{t('portalUi.admin.finance.form.description', 'Apraksts')}</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))} data-testid="finance-form-description" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} data-testid="finance-form-cancel">
                {t('portalUi.admin.finance.cancel', 'Atcelt')}
              </Button>
              <Button type="submit" data-testid="finance-form-save">
                {t('portalUi.admin.finance.save', 'Saglabāt')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceManagement;
